import React, { useEffect } from 'react'
import { UserServiceHelper } from '../modules/User'
import EMTShadowOptinsHelper from '@/helpers/EmtShadowOptinsHelper'
import { useEMTDispatch, useEMTState } from '@/store'
import { EmtCheckbox } from 'emt-components-v2'
import Box from '@mui/material/Box'
import Link from 'next/link'
import { useIsQueryOwner } from '@/hooks'
import { ShadowOptin, SODesktopScreenSlugs } from '@/data'
import { ObjectKeys } from '@/utils'
import useCurrentUser from '@/hooks/useCurrentUser'

const useShadowOptins = (
  screenSlug: SODesktopScreenSlugs,
  screenValues?: Record<string, any>,
  baseOptions?: {
    defaultChecked?: string[]
    enableLogs?: boolean
    manualUpdate?: boolean
  },
) => {
  const [isQueryOwner] = useIsQueryOwner()
  if (screenValues) {
    screenValues.isQueryOwner = isQueryOwner
  }
  const { currentShadowOptins } = useEMTState().user
  const { currentPendingUser } = useCurrentUser()
  const dispatch = useEMTDispatch()
  const { defaultChecked = [], enableLogs = false, manualUpdate = false } = baseOptions || {}

  const shadowOptins = EMTShadowOptinsHelper.filterShadowOptinsToApply(
    currentShadowOptins,
    currentPendingUser,
    screenSlug,
    {
      screenValues,
      enableLogs,
    },
  )

  const [checkedOptins, setCheckedOptins] = React.useState<Record<string, boolean>>({})

  const onSettingValueChange = (e: React.ChangeEvent<HTMLInputElement>, optinId: string) => {
    setCheckedOptins((prev) => {
      const newValues = { ...prev, [optinId]: e.target.checked }

      if (!manualUpdate) {
        dispatchOptins(currentPendingUser, newValues).finally()
      }

      return newValues
    })
  }

  useEffect(() => {}, [checkedOptins, manualUpdate])

  useEffect(() => {
    const defaultCheckedOptins: Record<string, boolean> = {}
    defaultChecked.forEach((id) => {
      defaultCheckedOptins[id] = true
    })
    setCheckedOptins(() => defaultCheckedOptins)
  }, [])

  const dispatchOptins = async (user = currentPendingUser, finalCheckedOptins = checkedOptins) => {
    if (user?.registrationType === 'borrower') {
      const optins = ObjectKeys(finalCheckedOptins).reduce((acc, optinId) => {
        if (finalCheckedOptins[optinId]) {
          acc.push(optinId)
        }
        return acc
      }, [] as string[])

      if (optins.length) {
        await UserServiceHelper.dispatchContinueRegisterAsync(dispatch)({
          type: 'borrower',
          body: { optins },
        })
      }
    }
  }

  const CaptionComponent = ({ optin }: { optin: ShadowOptin }) => {
    if (!optin.urlHref) return <>{optin.description}</>
    const optinDescSplit = optin.description.split('%link%')
    return (
      <>
        {optinDescSplit[0]}{' '}
        <Link href={optin.urlHref} target="_blank">
          {optin.urlText}
        </Link>{' '}
        {optinDescSplit[1]}
      </>
    )
  }

  const renderOptins = (color?: string) => {
    if (!checkedOptins) return

    return shadowOptins.map((optin, key) => (
      <Box
        key={key}
        sx={{
          '& .MuiBox-root': { lineHeight: '1rem', '& span': { lineHeight: '0px' }, '& p': { lineHeight: '1rem' } },
        }}
      >
        <EmtCheckbox
          label={optin.title}
          caption={<CaptionComponent optin={optin} />}
          checked={checkedOptins[optin.id]}
          value={'checked'}
          onChange={(e) => onSettingValueChange(e, optin.id)}
          variant={color || 'primary'}
        />
      </Box>
    ))
  }

  return { shadowOptins, checkedOptins, renderOptins, dispatchOptin: dispatchOptins }
}

export default useShadowOptins
